// Pixar Singing Ad's form (live's `pixar-singing-ad-form/library-view`, spec G14-pixar-singing-ad;
// live's lyrics stage captured (captures 159 to 169), its Song and Cast & set stages never were,
// so built from live's code): live's staged form in three approvals. The lyrics stage picks the
// product (one of the brand's, or an uploaded packshot) and the character (an AI Actor or a photo,
// or none: the director designs one), writes the lyrics (the server's pack reader and director,
// or the member) and approves them; the song stage makes the takes, each checked, and picks one;
// the cast stage draws the character, a frame a shot and the end card; Generate final video starts
// the run. The draft is the server's (`pixar-singing-draft.ts`).
import { Button, Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { ArrowLeft, Mic, Monitor, Music2, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetCatalog, useListTemplateCaptionStyles } from "../../../api/generated/endpoints/generation/generation";
import type { PixarLyrics } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  pixarSingingAdInputsCtaMax,
  pixarSingingAdInputsProblemMax,
  pixarSingingAdInputsPronunciationMax,
  type PixarSingingAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings } from "../../../platform/actors/use-makings";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { CastTile, sourceKey, type PickedPictures } from "./PodcastTiles";
import { InputChip } from "./PodcastScript";
import { CastStage, LyricsSection, SongStage } from "./PixarSingingStages";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { Subtitles } from "./Subtitles";
import { planStills, stillWaiting, usePixarDraft, videoBody, type DraftWork, type VideoSettings } from "./pixar-singing-draft";
import {
  GENERATE_LYRICS,
  blankLyrics,
  lyricsIssue,
  lyricsMeta,
  sameLyrics,
  songSeconds,
} from "./pixar-singing-lyrics";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot, or upload a packshot — the pack the character holds and the end card shows.",
  name: "The product name the song and the end card use. Leave empty and it is read off the packshot.",
  text:
    "A description or the product page. Every product fact in the lyrics is quoted from it — leave " +
    "empty and the words printed on the pack are used.",
};
const CHARACTER_TOOLTIP =
  "The one character who acts the song out. Pick an AI Actor or a photo and it becomes an animated " +
  "character with the same face, hair and clothes — leave empty and the director designs one for " +
  "your product's buyer.";
const MUSIC_STYLE_TOOLTIP = "The style of the jingle. Light, sparse styles keep the sung words clearest.";
const SINGER_TOOLTIP =
  "One solo vocalist who sings the lyrics and speaks the punch line. The character on screen never sings.";
const SUBTITLES_TOOLTIP =
  "Burned-in captions of the sung lyrics in short, big lines — white, the sung word in yellow — " +
  "over the shots only, not the end card.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about 40% of 1080p.";
const FORMAT_TOOLTIP = "Every frame and clip is vertical 9:16.";
const PICK_PRODUCT = "Pick a product and its packshot";
const ACTOR_PENDING = "Wait for the AI Actor to finish";
const PICK_TAKE = "Pick a song take";
const STILLS_PENDING = "Wait for every image";
const UNREADABLE_PACK = "The pack was hard to read — type the product name or its text and generate again.";
const APPROVE_LYRICS = "Approve lyrics";
const APPROVE_SONG = "Approve song";
const GENERATE_VIDEO = "Generate final video";
const HANDOFF_NOTICE = "Your Pixar singing ad is finishing in My Generations — you can close this panel now.";
/** Ours (live has no way back from the song: G14-pixar-singing-ad, Ours versus live 3). */
const BACK_TO_LYRICS = "Back to lyrics";
const BACK_TO_LYRICS_CONFIRM = "Edit the lyrics again? The takes are forgotten and made again on approval.";
/** Live's presets' caption style for this card where the form picked none. */
const DEFAULT_CAPTION_STYLE = "karaoke";
/** Live's music styles in its order (`director/music-styles.json`), and its singers. */
const MUSIC_STYLES = [
  { value: "showtune", label: "Broadway showtune" },
  { value: "pop_jingle", label: "Bright pop jingle" },
  { value: "acoustic", label: "Acoustic singer-songwriter" },
  { value: "country", label: "Feel-good country" },
  { value: "swing", label: "Retro swing / lounge" },
  { value: "kids_tv", label: "Cheerful kids-TV theme" },
  { value: "rnb", label: "Smooth R&B" },
] as const;
const SINGERS = [
  { value: "female", label: "Female singer" },
  { value: "male", label: "Male singer" },
] as const;
const RESOLUTIONS = [
  { value: "1080p", label: "1080p" },
  { value: "720p", label: "720p" },
] as const;
const CHIPS = [
  {
    field: "problem",
    label: "Problem",
    placeholder: "The afternoon sugar crash (optional)",
    tooltip:
      'The everyday problem the song sweetly celebrates before "said no one ever", e.g. "the ' +
      'afternoon sugar crash". Leave empty and the director takes it from the product text.',
    maxLength: pixarSingingAdInputsProblemMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button on the end card, word for word. Leave empty and the director writes a short one.",
    maxLength: pixarSingingAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "GO-lee (optional)",
    tooltip:
      "How your brand name is sung, e.g. GO-lee. Leave empty and the director decides what needs a " +
      "respelling.",
    maxLength: pixarSingingAdInputsPronunciationMax,
  },
] as const;

/** The character "Add AI Actor" is making (live's `character.generation`). */
interface CharacterMaking {
  jobId: string;
  failed: boolean;
}

/** Live's progress line under the form (`run-progress.ts`): one director at a time, else the
 * takes and the images counted. */
function progressOf(work: DraftWork, draft: { takes: { status: string }[]; stills: { status: string }[] }) {
  if (work.reading) return "Reading the pack";
  if (work.writing) return "Writing the lyrics";
  if (work.planning) return "Planning the shots";
  const lines: string[] = [];
  if (work.takes) {
    const done = draft.takes.filter((take) => take.status !== "working").length;
    lines.push(`Making song takes · ${done}/${draft.takes.length}`);
  }
  if (work.drawing) {
    const done = draft.stills.filter((still) => still.status === "done").length;
    lines.push(`Generating images · ${done}/${draft.stills.length}`);
  }
  return lines.length === 0 ? null : lines.join(" · ");
}

/**
 * Live's form: "Reuse example" (off and disabled until live's record is read, the spec's open
 * point 6), the stage, and in the footer the subtitles (Karaoke by default), the music style and
 * the singer (locked once the lyrics are approved), the resolution (free until the video starts),
 * the 9:16 format and the round start: "Approve lyrics", "Approve song", then "Generate final
 * video", whose tooltip says what it waits for.
 */
export function PixarSingingAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = usePixarDraft(teamId, brandId);
  const catalog = useGetCatalog().data;
  const styles = useListTemplateCaptionStyles(teamId).data;
  const [composing, setComposing] = useState(false);
  const [making, setMaking] = useState<CharacterMaking | null>(null);
  const [pictures, setPictures] = useState<PickedPictures>({});
  const [baseline, setBaseline] = useState<PixarLyrics | null | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [wasWriting, setWasWriting] = useState(false);
  const [productAsked, setProductAsked] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [confirmingBack, setConfirmingBack] = useState(false);
  // The video's settings once the lyrics are approved (the draft takes no change then). Back
  // to lyrics drops them, so a later approval reads the draft's picks again, not these.
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  const { form, draft } = flow;
  const writing = flow.work.reading || flow.work.writing;
  const approved = draft !== undefined && draft.stage !== "lyrics";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? {
          subtitles: form.inputs.subtitles,
          caption_position: form.inputs.caption_position,
          caption_style_id: form.inputs.caption_style_id,
          resolution: form.inputs.resolution,
        });
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted,
  });
  if (draft !== undefined && baseline === undefined) setBaseline(draft.lyrics);
  if (writing !== wasWriting) {
    setWasWriting(writing);
    if (!writing && draft !== undefined && draft.lyrics_director !== "failed") setBaseline(draft.lyrics);
  }

  const setInputs = (change: Partial<PixarSingingAdInputsOutput>) => flow.editInputs(change);
  const pick = (actor: PickedActor) => {
    setPictures((current) => ({ ...current, [sourceKey(actor.source)]: { url: actor.imageUrl, name: actor.name } }));
    setInputs({ character: actor.source });
  };
  useMakings({
    teamId,
    brandId,
    makings: making === null ? [] : [making],
    onMade: (_jobId, made) => {
      setMaking(null);
      pick(pickedOfActor(made));
    },
    onFailed: () => setMaking((current) => (current === null ? null : { ...current, failed: true })),
  });

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Pixar Singing Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const makers = catalog === undefined ? [] : actorModels(catalog.image_models);
  if (composing && catalog !== undefined) {
    const close = () => setComposing(false);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(job) => {
          setMaking({ jobId: job.id, failed: false });
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const actor = made[0];
            if (actor !== undefined) pick(pickedOfActor(actor));
            close();
          },
        }}
      />
    );
  }

  const { inputs, lyrics } = form;
  const locked = !flow.editable;
  const work = flow.work;
  const progress = progressOf(work, draft);
  const issue = lyricsIssue(lyrics, inputs.cta);
  const edited = lyrics !== null && !sameLyrics(lyrics, baseline ?? null);
  const shownIssue = lyrics !== null && !writing && (edited || submitted) ? issue : null;
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const pickedTake = picked ?? draft.picked_take;

  let submit;
  if (draft.stage === "song") {
    const take = draft.takes.find((each) => each.number === pickedTake);
    const ready = take?.pickable === true;
    submit = (
      <Submit
        label={APPROVE_SONG}
        reasons={ready ? [] : [PICK_TAKE]}
        disabled={!ready || flow.busy}
        loading={flow.approving}
        onClick={() => {
          if (pickedTake !== null) flow.approveSong(pickedTake);
        }}
      />
    );
  } else if (draft.stage === "cast") {
    const ready = video !== null;
    const starting = videoStart.start.isPending;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || starting}
        loading={starting}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(lyrics === null ? [GENERATE_LYRICS] : []),
      ...(making !== null ? [ACTOR_PENDING] : []),
    ];
    submit = (
      <Submit
        label={APPROVE_LYRICS}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={() => {
          setSubmitted(true);
          flow.approveLyrics();
        }}
      />
    );
  }

  const styleId = shownSettings.caption_style_id || DEFAULT_CAPTION_STYLE;
  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Subtitles
          value={{ on: shownSettings.subtitles, position: shownSettings.caption_position, styleId }}
          styles={styles?.styles}
          tip={SUBTITLES_TOOLTIP}
          disabled={flow.busy}
          onChange={(value) =>
            changeSettings({
              subtitles: value.on,
              caption_position: value.position,
              caption_style_id: value.styleId,
            })
          }
        />
        <Choice
          variant="footer"
          setting="Music style"
          icon={<Music2 size={16} strokeWidth={1.5} />}
          tip={MUSIC_STYLE_TOOLTIP}
          width={196}
          value={inputs.music_style}
          options={MUSIC_STYLES.map((each) => ({ value: each.value, label: each.label }))}
          disabled={locked}
          onChange={(next) => {
            const style = MUSIC_STYLES.find((each) => each.value === next);
            if (style !== undefined) setInputs({ music_style: style.value });
          }}
        />
        <Choice
          variant="footer"
          setting="Singer"
          icon={<Mic size={16} strokeWidth={1.5} />}
          tip={SINGER_TOOLTIP}
          width={136}
          value={inputs.singer}
          options={SINGERS.map((each) => ({ value: each.value, label: each.label }))}
          disabled={locked}
          onChange={(next) => {
            const singer = SINGERS.find((each) => each.value === next);
            if (singer !== undefined) setInputs({ singer: singer.value });
          }}
        />
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={88}
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((each) => ({ value: each.value, label: each.label }))}
          disabled={flow.busy}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each.value === next);
            if (resolution !== undefined) changeSettings({ resolution: resolution.value });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {videoStart.start.isSuccess && draft.stage === "cast" ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );

  if (draft.stage === "song") {
    const asked = draft.lyrics === null ? 0 : songSeconds(draft.lyrics);
    return panel(
      <div className={classes.content}>
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<ArrowLeft size={14} aria-hidden />}
            disabled={flow.busy}
            onClick={() => setConfirmingBack(true)}
          >
            {BACK_TO_LYRICS}
          </Button>
        </Group>
        <SongStage
          takes={draft.takes}
          seconds={asked}
          note={draft.takes_note}
          picked={pickedTake}
          busy={flow.busy}
          onPick={setPicked}
          onAddTake={flow.addTake}
        />
        {status}
        <ConfirmModal
          opened={confirmingBack}
          title={BACK_TO_LYRICS}
          confirmLabel={BACK_TO_LYRICS}
          pending={false}
          onCancel={() => setConfirmingBack(false)}
          onConfirm={() => {
            setPicked(null);
            setSettings(null);
            flow.backToLyrics();
            setConfirmingBack(false);
          }}
        >
          <Text size="sm">{BACK_TO_LYRICS_CONFIRM}</Text>
        </ConfirmModal>
      </div>,
      footer,
    );
  }
  if (draft.stage === "cast") {
    return panel(
      <div className={classes.content}>
        <CastStage
          stills={planStills(draft)}
          timings={draft.timings}
          waiting={(name) => stillWaiting(name, draft)}
          busy={flow.busy}
          onRegenerate={flow.regenerate}
          onBackToTakes={() => {
            setPicked(null);
            flow.backToTakes();
          }}
        />
        {status}
      </div>,
      footer,
    );
  }

  const warnings = [
    ...(draft.pack_warning ? [UNREADABLE_PACK] : []),
    ...(draft.lyrics_warning !== null && lyrics !== null ? [draft.lyrics_warning] : []),
  ];
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
          <CastTile
            teamId={teamId}
            brandId={brandId}
            label="Character"
            tooltip={CHARACTER_TOOLTIP}
            source={inputs.character ?? null}
            making={making}
            pictures={pictures}
            disabled={locked}
            onMakeActor={makers.length === 0 ? null : () => setComposing(true)}
            onPick={pick}
            onRemove={() => {
              setMaking(null);
              setInputs({ character: null });
            }}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<PixarSingingAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <LyricsSection
          lyrics={lyrics}
          meta={lyrics === null ? null : lyricsMeta(lyrics)}
          writing={writing}
          locked={locked}
          issue={shownIssue}
          warning={warnings.length === 0 ? null : warnings.join(" ")}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateLyrics();
          }}
          onWrite={() => {
            const blank = blankLyrics();
            setBaseline(blank);
            flow.edit({ lyrics: blank });
          }}
          onLine={(index, text) => {
            if (lyrics === null) return;
            flow.edit({
              lyrics: { ...lyrics, lines: lyrics.lines.map((line, at) => (at === index ? { ...line, text } : line)) },
            });
          }}
          onTag={(tag) => {
            if (lyrics !== null) flow.edit({ lyrics: { ...lyrics, tag } });
          }}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
